import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {shuffleArray} from 'helpers/array-helper';
import {getText} from 'helpers/language-helper';
import TaskIntro from '../task-intro/task-intro';
import Audio from 'components/ui/audio/audio';
import './branching-story-mc.scss';

const BranchingStoryMc = (props) => {
	/* Props */
	const {
		languageId, 
		taskId, 
		moduleId,
		fadeLevel, 
		deviceInfo,
		playerStoryStepData, 
		storyStepData, 
		handleCompleteStoryStep,
		handleInstantTaskEffects
	} = props;

	/**
	 * Get option ids, shuffle them
	 * @returns {array} optionIds
	 */
	const getOptionIds = () => {
		if (!storyStepData.options) return [];
		return shuffleArray(storyStepData.options.map((option) => {return option.id;}));
	};
	const [optionIds, setOptionIds] = useState(getOptionIds());

	/**
	 * Get selected option id
	 * @returns {number} optionId
	 */
	const getSelectedOptionId = () => {
		const optionId = (playerStoryStepData && playerStoryStepData.optionId
			?  playerStoryStepData.optionId
			: null
		);
		return optionId;
	};

	/* Selected option id, points, feedback */
	const [selectedOptionId, setSelectedOptionId] = useState(getSelectedOptionId());
	const [points, setPoints] = useState(0);

	/* Update selected items if new step */
	useEffect(() => {
		setOptionIds(getOptionIds());
		setSelectedOptionId(getSelectedOptionId());
		setPoints(0);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storyStepData.id]);


	/**
	 * Select option
	 * @param {number} optionId 
	 * @returns 
	 */
	const selectOptionId = (optionId) => {
		/* Already completed / selected */
		if (playerStoryStepData.isCompleted || selectedOptionId) return;
		
		/* Get selected option data */
		const selectedOptionData = (storyStepData.options 
			? storyStepData.options.find((o) => {return o.id === optionId;})
			: 0
		);
		if (selectedOptionData) {
			setSelectedOptionId(optionId);

			let newPoints = points;
			let instantEffects = [];

			if (selectedOptionData.effects && selectedOptionData.effects.length > 0) {
				selectedOptionData.effects.forEach((effect, effectIndex) => {
					if (effect.type === 'points') {
						/* Effect: points */
						newPoints += effect.value;	
					} else if (effect.type === 'feedback' && effect.feedback) {
						let effectObj = JSON.parse(JSON.stringify(effect));
						effectObj.feedback.moduleId = moduleId;
						effectObj.feedback.audioFileName = languageId + '-' + taskId 
							+ '-' + selectedOptionData.id + '-' + effect.type + '-' + (effectIndex + 1);
						instantEffects.push(effectObj);
					}
				});
			}
			setPoints(newPoints);
			if (instantEffects.length > 0) {
				handleInstantTaskEffects(instantEffects);
			}

			/* Complete step */
			completeStoryStep(optionId, newPoints);
		}
	};

	/**
	 * Complete story step
	 * @param {number} optionId 
	 */
	const completeStoryStep = (optionId, newPoints) => {
		const selectedOptionData = (storyStepData.options 
			? storyStepData.options.find((o) => {return o.id === optionId;})
			: 0
		);
		if (selectedOptionData) {
			/* Complete step */
			handleCompleteStoryStep(newPoints, selectedOptionData.nextStepId, optionId);
		}
	};


	return (
		<div className={'BranchingStoryMc ' + deviceInfo.orientation + (fadeLevel ? ' ' + fadeLevel + 'FadeOut' : '')}>
			{storyStepData.text && <div className="BranchingStoryMc-intro">
				<TaskIntro 
					taskType={'branching-story-mc'}
					text={getText(storyStepData.text, languageId)}
					image={storyStepData.image}
					fileName={languageId + '-' + taskId + '-' + storyStepData.id}
				/>
			</div>}
			<div className="BranchingStoryMc-options">
				{optionIds.map((optionId) => {
					const optionData = storyStepData.options.find((option) => {return option.id === optionId;});
					if (!optionData) return null;

					const isSelected = (selectedOptionId === optionData.id);
					const isHidden = (!isSelected && playerStoryStepData.isCompleted === true);

					const optionClass = 'BranchingStoryMc-option' 
						+ (isSelected ? ' selected' : '' + (isHidden ? ' hidden' : ''));
					return (
						<div 
							key={optionData.id} 
							className={optionClass} 
							onClick={() => {selectOptionId(optionData.id);}}
						>
							<span>{getText(optionData.text, languageId)}</span>
							<div className="BranchingStoryMc-audio">
								<Audio 
									type='task-option'
									languageId={languageId}
									moduleId={moduleId}
									fileName={languageId + '-' + taskId + '-' 
										+ storyStepData.id + '-option-' + optionData.id}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

BranchingStoryMc.propTypes = {
	languageId: PropTypes.string.isRequired,
	taskId: PropTypes.string.isRequired,
	moduleId: PropTypes.string.isRequired,
	fadeLevel: PropTypes.string,
	deviceInfo: PropTypes.object.isRequired,
	playerStoryStepData: PropTypes.object,
	storyStepData: PropTypes.object.isRequired,
	handleCompleteStoryStep: PropTypes.func.isRequired,
	handleInstantTaskEffects: PropTypes.func.isRequired
};

export default BranchingStoryMc;
